// src/pages/MapPage/MapPage.tsx

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import MapComponent from "../../components/Map/Map";
import type { HealthCenter } from "../../components/Map/Map";

const MapPage: React.FC = () => {
  const [centers, setCenters] = useState<HealthCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:5000";

  /* ---------- Fetch Health Centers ---------- */
  const fetchAndCacheData = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/health-centers`);
      if (!res.ok) throw new Error("Failed to fetch health centers");

      const data = await res.json();
      setCenters(data);

      sessionStorage.setItem("mappage_centers", JSON.stringify(data));
      sessionStorage.setItem("mappage_timestamp", Date.now().toString());
    } catch (err) {
      console.error("Error fetching health centers:", err);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [API_BASE_URL]);

  /* ---------- Cache Logic ---------- */
  useEffect(() => {
    const cachedCenters = sessionStorage.getItem("mappage_centers");
    const cachedTime = sessionStorage.getItem("mappage_timestamp");
    const now = Date.now();

    if (cachedCenters && cachedTime && now - +cachedTime < 60 * 60 * 1000) {
      setCenters(JSON.parse(cachedCenters));
      setLoading(false);
    } else {
      fetchAndCacheData();
    }
  }, [fetchAndCacheData]);

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#1B2431",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  /* ---------- Render ---------- */
  return (
    <Box
      sx={{
        position: "relative",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: "#1B2431",
      }}
    >
      {/* Full-screen Map */}
      <MapComponent centers={centers} />

      {/* Refresh Button – Floating Top-Right */}
      <Paper
        elevation={6}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 1000,
          borderRadius: "50%",
          p: 1,
          bgcolor: "rgba(39, 49, 66, 0.9)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Tooltip title="Refresh Map Data" arrow placement="left">
          <span>
            <IconButton
              onClick={fetchAndCacheData}
              disabled={refreshing}
              sx={{
                color: "#FFFFFF",
                "&:hover": {
                  bgcolor: "rgba(72, 128, 255, 0.2)",
                },
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Paper>
    </Box>
  );
};

export default MapPage;